import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import CheckIcon from '@mui/icons-material/Check';
import { SxProps, Theme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGradeLabel } from '../../hooks/useGradeLabel';
import { useOrderedGrades } from '../../hooks/useOrderedGrades';
import { gradeTranslateAttr } from '../../lib/gradeLabel';
import { pillSelectSx } from './headerPill';

interface GradePillSelectProps {
  /** Extra styles merged over the base pill look. */
  sx?: SxProps<Theme>;
  /** Cap the dropdown height so long lists scroll. */
  menuMaxHeight?: number;
  /** Hide until a grade is chosen (Header); else always show. */
  requireGradeSelected?: boolean;
}

/** Grade picker pill Select. Reads/writes the `grade` search param. */
const GradePillSelect = ({
  sx, menuMaxHeight, requireGradeSelected = false,
}: GradePillSelectProps) => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const orderedGrades = useOrderedGrades();
  const getGradeLabel = useGradeLabel();

  const grade = params.get('grade') || '';
  const gradeOption = orderedGrades.find((g) => g.value === grade);
  const gradeLabel = gradeOption
    ? getGradeLabel(gradeOption)
    : { text: grade, allowGoogleTranslate: true };

  const handleGradeChange = (nextGrade: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('grade', nextGrade);
      return next;
    });
  };

  if (orderedGrades.length === 0) return null;
  if (requireGradeSelected && !grade) return null;

  return (
    <Select
      value={grade}
      onChange={(e) => handleGradeChange(e.target.value)}
      inputProps={{ 'aria-label': t('finder.search_grade') }}
      renderValue={() => (
        <Box component="span" translate={gradeTranslateAttr(gradeLabel)}>{gradeLabel.text}</Box>
      )}
      MenuProps={menuMaxHeight ? { PaperProps: { sx: { maxHeight: menuMaxHeight } } } : undefined}
      sx={[pillSelectSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
      size="small"
    >
      {orderedGrades.map((g) => {
        const label = getGradeLabel(g);
        return (
          <MenuItem key={g.value} value={g.value} translate={gradeTranslateAttr(label)} sx={{ padding: '12px !important' }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1,
            }}
            >
              <Box component="span" translate={gradeTranslateAttr(label)}>{label.text}</Box>
              {grade === g.value && <CheckIcon sx={{ fontSize: 16 }} />}
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default GradePillSelect;
